import { Checkbox, Chip, FormControl, FormHelperText, FormLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import HelpTooltip from '../HelpTooltip';
import { BiHelpCircle } from 'react-icons/bi';
import { useEffect, useState } from 'react';
import { FbeOption } from '../../services/fbeTypes';
import { Close, QueryStats } from '@mui/icons-material';
import FieldLock from '../FieldLock';
import ImageTooltip from '../ImageTooltip';

export interface SingleSelectProps {
    labelText: string;
    labelId: string;
    mandatory: boolean;
    helpTextTitle: string;
    helpTextBody: string;
    onChange: (event: any) => void;
    options: FbeOption[];
    selectedValues: FbeOption[];
    disabled: boolean;
    fullWidth: boolean;
    error?: boolean;
    helperText?: string;

    onLock: (field: string, locked: boolean) => void;
    disabledLock: boolean;
    displayedLock: boolean;
    isLocked: boolean;

    showWorkflowInfo?: boolean;
}

const SingleSelect = (props: SingleSelectProps): JSX.Element => {
    const [selectedValue, setSelectedValue] = useState<string>('');
    const [open, setOpen] = useState(false);
    const [prev, setPrev] = useState('');

    useEffect(() => {
      if(props.selectedValues.length > 0){
        setSelectedValue(props.selectedValues[0].value);
      } else {
        setSelectedValue('');
        setPrev('');
      }
    }, [props.selectedValues]);

    const handleInputChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value as string;

        if( (prev === value) && !props.mandatory){
          setPrev('');
          setSelectedValue('');
          props.onChange('value');
        } else {
          setPrev(value);
          setSelectedValue(value);
          props.onChange(value);
        }

        setOpen(false);
    }

    const handleClose = () => {
        setOpen(false);
    }

    const handleDelete = () => {
      setPrev('');
      setSelectedValue('');
      props.onChange('');
      setOpen(false);
    }

    return (
      <FormControl
          fullWidth={props.fullWidth}
          error={props.error || false}
      >
      <FormLabel component="legend" sx={{fontSize:'12px',mb:1.5}} htmlFor={props.labelId}>
      <FieldLock
          onChange={function (locked: boolean): void {
              props.onLock(props.labelId,locked);
          } }
          labelId={props.labelId}
          disabled={props.disabledLock}
          displayed={props.displayedLock}
          value={props.isLocked}/>
        {props.labelText} <b>{props.mandatory ? '*' : ''}</b> <HelpTooltip title={props.helpTextTitle} body={props.helpTextBody}> <BiHelpCircle/> </HelpTooltip>
            {props.showWorkflowInfo !== undefined && props.showWorkflowInfo ? (
              <ImageTooltip image={'workflow.png'} imageAltText={'workflow image overview'}> <QueryStats sx={{ fontSize: 16 }}/> </ImageTooltip>
            ) : (<></>)}
        </FormLabel>
      <Select
        id={props.labelId}
        name={props.labelId}
        data-testid={props.labelId}
        sx={{bgcolor:'#FFFFFF', width:'350px', flexGrow:'initial', height:'50px'}}
        value={selectedValue}
        onChange={handleInputChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        inputProps={{
          'aria-label': props.labelText,
          autoComplete: 'off'
        }}
        renderValue={(selected) => {
          const option = props.options.find((opt) => opt.value === selected);
          return option ? <Chip
                  id={option.value}
                  data-testid={option.label}
                  key={option.value}
                  label={option.label}
                  sx={{ margin: '2px' }}
                  onDelete={!props.disabled ? () => { handleDelete(); } : undefined}
                  deleteIcon={
                    !props.disabled ? (
                      <Close
                        sx={{
                          color: 'white',
                          fontSize: '14px',
                          borderRadius: '16px',
                          bgcolor: 'lightgray',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.3)',
                          },
                          '& .MuiChip-deleteIcon': {
                            color: 'white',
                            '&:hover': {
                              color: '#ffeb3b',
                            },
                          },
                        }}
                      />
                    ) : undefined
                  }
                  onMouseDown={(event) => { if (!props.disabled) event.stopPropagation(); }}
              /> : null;
          }}
        defaultValue=""
        fullWidth={props.fullWidth}
        disabled={props.disabled}
      >
      {props.options.map((option) => (
        <MenuItem
          id={`si${option.value}`} key={option.value} value={option.value} data-testid={`mi${option.label}`} disabled={option.disabled}
          onClick={() => {
            if ( (selectedValue === option.value )&& !props.mandatory) {
              handleDelete();
            } else {
              setPrev(option.value);
              setSelectedValue(option.value);
              props.onChange(option.value);
            }
          }}
          >
            {
              option.label.startsWith('==') ? (<></>) : (
                <Checkbox id={`c${option.value}`} checked={selectedValue === option.value} key={`${option.value}}`} data-testid={`cb${option.label}`} disabled={option.disabled} />
              )
            }
          {option.label}
        </MenuItem>
      ))}
      </Select>
      {props.helperText && <FormHelperText>{props.helperText}</FormHelperText>}
    </FormControl>
  );
};

export default SingleSelect;